'use client'

import * as React from 'react'

import { cn } from '../../lib'

interface Badge {
  label: string
  variant?: 'default' | 'blue' | 'green' | 'red' | 'orange'
}

interface FeedbackListItem {
  id: string
  badges: Badge[]
  title: React.ReactNode
  description?: React.ReactNode
  content?: React.ReactNode
}

interface FeedbackListProps {
  items: FeedbackListItem[]
  className?: string
  itemClassName?: string
  showDivider?: boolean
}

const FeedbackList = React.forwardRef<HTMLDivElement, FeedbackListProps>(
  ({ className, items, itemClassName, showDivider = true, ...props }, ref) => {
    const getBadgeColor = (variant?: Badge['variant']) => {
      switch (variant) {
        case 'blue':
          return 'bg-blue-50 text-blue-600 border-blue-200'
        case 'green':
          return 'bg-green-50 text-green-600 border-green-200'
        case 'red':
          return 'bg-red-50 text-red-600 border-red-200'
        case 'orange':
          return 'bg-orange-50 text-orange-600 border-orange-200'
        default:
          return 'bg-white text-gray-700 border-gray-200'
      }
    }

    return (
      <div ref={ref} className={cn('p-5 bg-gray-100 rounded-lg border-gray-200 border', className)} {...props}>
        <div className="space-y-5">
          {items.map((item, index) => (
            <div key={item.id} className={cn('space-y-2', itemClassName)}>
              {/* Badges */}
              {item.badges.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {item.badges.map((badge, badgeIndex) => (
                    <div
                      key={badgeIndex}
                      className={cn(
                        'inline-flex items-center rounded-[14px] px-3 py-[3px] border text-sm font-medium',
                        getBadgeColor(badge.variant),
                      )}
                    >
                      {badge.label}
                    </div>
                  ))}
                </div>
              )}

              {/* Content */}
              <div className="space-y-0.5">
                {item.title && <div className="text-base font-medium text-gray-900">{item.title}</div>}
                {item.description && <div className="text-sm text-gray-500">{item.description}</div>}
                {item.content && <div className="text-sm text-gray-700">{item.content}</div>}
              </div>

              {/* Divider */}
              {showDivider && index < items.length - 1 && <div className="border-t border-dashed border-gray-300" />}
            </div>
          ))}
        </div>
      </div>
    )
  },
)

FeedbackList.displayName = 'FeedbackList'

export { FeedbackList, type FeedbackListItem, type FeedbackListProps }
